import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadMbListStatus: 'idle',
    loadMbListError: undefined as any,
    loadMbListLastArgs: undefined as any,
    loadMbStatus: 'idle',
    loadMbError: undefined as any,
    saveMbStatus: 'idle',
    saveMbError: undefined as any,
    deleteMbStatus: 'idle',
    deleteMbError: undefined as any,

    mbList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadMbList = createAsyncThunk('mbSlice/loadMbList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().mbSlice.loadMbListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/mb/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadMbListStatus } = getState().mbSlice
        return loadMbListStatus !== 'pending'
    }
})

export const loadMb = createAsyncThunk('mbSlice/loadMb', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/mb/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadMbStatus } = getState().mbSlice
        return loadMbStatus !== 'pending'
    }
})

export const saveMb = createAsyncThunk('mbSlice/saveMb', async ({ mb }: any, { rejectWithValue }: any) => {
    let promises = []
    if (mb?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/mb/${ mb?.HDR_ID }/`,
            prepareData(mb),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/mb/`,
            prepareData(mb),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveMbStatus } = getState().mbSlice
        return saveMbStatus !== 'pending'
    }
})

export const deleteMb = createAsyncThunk('mbSlice/deleteMb', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/mb/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteMbStatus } = getState().mbSlice
        return deleteMbStatus !== 'pending'
    }
})

const MbSlice = createSlice({
    name: 'mbSlice',
    initialState,
    reducers: {
        resetloadMbListError: (state) => {
            state.loadMbListError = undefined
        },
        resetloadMb: (state) => {
            state.formItem = undefined
            state.loadMbError = undefined
        },
        resetloadMbError: (state) => {
            state.loadMbError = undefined
        },
        resetsaveMb: (state) => {
            state.formItem = undefined
            state.saveMbError = undefined
        },
        resetsaveMbError: (state) => {
            state.saveMbError = undefined
        },
        resetdeleteMbError: (state) => {
            state.deleteMbError = undefined
        },
        removeFromMbListById: (state, action) => {
            state.mbList = state.mbList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromMbListByIds: (state, action) => {
            state.mbList = state.mbList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.mbList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadMbListStatus = 'preloaded'
            state.loadMbListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.mbList = []
            state.loadMbListStatus = 'idle'
            state.loadMbListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadMbList.pending, (state) => {
            state.loadMbListStatus = 'pending'
            state.loadMbListError = undefined
        })
        builder.addCase(loadMbList.fulfilled, (state, action) => {
            state.loadMbListStatus = 'idle'

            if (!action.payload) { return }

            state.loadMbListLastArgs = action.payload.args

            {
                state.mbList = action.payload.data

            }
        })
        builder.addCase(loadMbList.rejected, (state, action) => {
            state.loadMbListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadMbListError = action.error.message
            } else {
                state.loadMbListError = action.payload
            }
        })

        builder.addCase(loadMb.pending, (state) => {
            state.loadMbStatus = 'pending'
            state.loadMbError = undefined
        })
        builder.addCase(loadMb.fulfilled, (state, action) => {
            state.loadMbStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.mbList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.mbList.push(newItem) }
                else { state.mbList[i] = { ...state.mbList[i], ...newItem } }
            }
        })
        builder.addCase(loadMb.rejected, (state, action) => {
            state.loadMbStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadMbError = action.error.message
            } else {
                state.loadMbError = action.payload
            }
        })

        builder.addCase(saveMb.pending, (state) => {
            state.saveMbStatus = 'pending'
            state.saveMbError = undefined
        })
        builder.addCase(saveMb.fulfilled, (state, action) => {
            state.saveMbStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.mbList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.mbList.push(newItem) }
                else { state.mbList[i] = { ...state.mbList[i], ...newItem } }
            }
        })
        builder.addCase(saveMb.rejected, (state, action) => {
            state.saveMbStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveMbError = action.error.message
            } else {
                state.saveMbError = action.payload
            }
        })

        builder.addCase(deleteMb.pending, (state) => {
            state.deleteMbStatus = 'pending'
            state.deleteMbError = undefined
        })
        builder.addCase(deleteMb.fulfilled, (state, action) => {
            state.deleteMbStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.mbList = state.mbList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.mbList = state.mbList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteMb.rejected, (state, action) => {
            state.deleteMbStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteMbError = action.error.message
            } else {
                state.deleteMbError = action.payload
            }
        })

    }
})

export const {
    resetloadMbListError,
    resetloadMb,
    resetloadMbError,
    resetsaveMb,
    resetsaveMbError,
    resetdeleteMbError,
    removeFromMbListById,
    removeFromMbListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = MbSlice.actions
export default MbSlice.reducer
